import { useCallback, useState } from "react";
import { DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE, showToastError, showToastSuccess } from "@/utils";

export type RestoreOnCreateConflictConfig<
    TConflict extends { existingId: string },
    TPending,
    TRestorePayload,
    TDetail extends { id: string },
> = {
    isConflictError: (err: unknown) => err is { conflict: TConflict };
    isRestoreAvailable: (conflict: unknown) => conflict is TConflict;
    modal: { title: string; body: (conflict: TConflict) => string };
    buildRestorePayload: (conflict: TConflict, pending: TPending) => TRestorePayload;
    formatSuccessMessage: (conflict: TConflict) => string;
    restoreDeletedWithCreateValues: (payload: TRestorePayload) => Promise<TDetail>;
    onRestored: (detail: TDetail, conflict: TConflict) => void;
};

export function useRestoreOnCreateConflict<
    TConflict extends { existingId: string },
    TPending,
    TRestorePayload,
    TDetail extends { id: string },
>({
    isConflictError,
    isRestoreAvailable,
    modal,
    buildRestorePayload,
    formatSuccessMessage,
    restoreDeletedWithCreateValues,
    onRestored,
}: RestoreOnCreateConflictConfig<TConflict, TPending, TRestorePayload, TDetail>) {
    const [restoreConflict, setRestoreConflict] = useState<TConflict | null>(null);
    const [pendingRestoreForm, setPendingRestoreForm] = useState<TPending | null>(null);
    const [restoringDeleted, setRestoringDeleted] = useState(false);

    const handleCreateError = useCallback(
        (err: unknown): boolean => {
            if (isConflictError(err) && isRestoreAvailable(err.conflict)) {
                setRestoreConflict(err.conflict);
                return true;
            }
            return false;
        },
        [isConflictError, isRestoreAvailable]
    );

    const clearRestoreConflict = useCallback(() => {
        setRestoreConflict(null);
        setPendingRestoreForm(null);
    }, []);

    const confirmRestore = useCallback(
        async (confirmed: boolean) => {
            if (!confirmed || !restoreConflict || pendingRestoreForm === null) {
                clearRestoreConflict();
                return;
            }
            setRestoringDeleted(true);
            try {
                const restored = await restoreDeletedWithCreateValues(
                    buildRestorePayload(restoreConflict, pendingRestoreForm)
                );
                const conflict = restoreConflict;
                clearRestoreConflict();
                showToastSuccess(formatSuccessMessage(conflict));
                onRestored(restored, conflict);
            } catch {
                showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
            } finally {
                setRestoringDeleted(false);
            }
        },
        [
            restoreConflict,
            pendingRestoreForm,
            restoreDeletedWithCreateValues,
            buildRestorePayload,
            formatSuccessMessage,
            onRestored,
            clearRestoreConflict,
        ]
    );

    const restoreModalProps = {
        show: restoreConflict !== null && pendingRestoreForm !== null,
        title: modal.title,
        body: restoreConflict ? modal.body(restoreConflict) : "",
        buttonSuccessText: restoringDeleted ? "Restoring…" : "Yes",
        buttonCancelText: "No" as const,
    };

    return {
        restoreConflict,
        pendingRestoreForm,
        restoringDeleted,
        handleCreateError,
        setPendingRestoreForm,
        clearRestoreConflict,
        confirmRestore,
        restoreModalProps,
    };
}
